import { getBerlinDateISO } from "../../../utils/dateHelpers";

// ============================================================================
// Pure helper functions extracted from AttendanceDialog.tsx
// ============================================================================

/**
 * Format a date for the mobile header display.
 * e.g. "Mo, 10.03.26 08:00 Uhr"
 */
export const formatMobileHeaderDate = (date: Date, time?: string): string => {
  if (!date || isNaN(date.getTime())) {
    return time ? `${time} Uhr` : "Datum unbekannt";
  }
  const days = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  const d = days[date.getDay()];
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  return `${d}, ${dd}.${mm}.${yy}${time ? ` ${time} Uhr` : ""}`;
};

/**
 * Format a student's name for display, with optional class abbreviation on mobile.
 */
export const formatStudentName = (
  fullName: string,
  isMobile: boolean,
  isCourse: boolean,
): string => {
  if (!fullName) return fullName;
  const raw = String(fullName);
  const classMatch = raw.match(/\(([^)]+)\)\s*$/);
  const classSuffix = classMatch ? classMatch[1] : "";
  if (!isMobile) return raw;

  const cleaned = raw.replace(/\s*\([^)]*\)\s*$/, "").trim();
  const parts = cleaned.split(/\s+/);
  let base: string;
  if (parts.length >= 2) {
    // Mobile compact: keep only the FIRST first name (drop any middle
    // names) + first two letters of the last name. e.g.
    // "Miray Rumenova Mahmood" → "Miray Ma".
    const first = parts[0];
    const last = parts[parts.length - 1];
    const lastAbbr = last.replace(/[^A-Za-zÄÖÜäöüß]/g, "").slice(0, 2);
    base = `${first} ${lastAbbr}`.trim();
  } else {
    base = cleaned.slice(0, 2);
  }

  if (classSuffix && isCourse) {
    return `${base} (${classSuffix})`;
  }
  return base;
};

/** Get today's ISO date string in Berlin timezone */
export const getTodayISO = (): string => getBerlinDateISO();

/** Remove class suffix from a name for comparison */
export const cleanName = (n: string): string =>
  (n || "").replace(/\s*\([^)]*\)\s*$/, "").trim();

/** Compare two names for sorting, ignoring class suffix */
export const compareNames = (a: string, b: string): number =>
  cleanName(a).localeCompare(cleanName(b), "de", { sensitivity: "base" });

/**
 * Get the default "late" arrival time: lesson start + 5 minutes.
 */
export const getDefaultLateTime = (lesson: any): string => {
  if (!lesson) return "";
  const [startHours, startMinutes] = lesson.time.split(":").map(Number);
  const lessonStart = new Date();
  lessonStart.setHours(startHours, startMinutes, 0, 0);
  const defaultTime = new Date(lessonStart.getTime() + 5 * 60000);
  return new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/Berlin",
  }).format(defaultTime);
};

/**
 * Calculate how many minutes late a student arrived.
 */
export const calculateLateMinutes = (
  arrivalTime: string,
  lesson: any,
): number => {
  if (!arrivalTime || !lesson) return 5;
  try {
    const [ah, am] = arrivalTime.split(":").map((n: string) => parseInt(n, 10));
    const start = new Date(lesson.start_datetime);
    const arrival = new Date(start);
    arrival.setHours(ah, am || 0, 0, 0);
    const diff = Math.max(
      0,
      Math.round((arrival.getTime() - start.getTime()) / 60000),
    );
    return diff || 5;
  } catch {
    return 5;
  }
};

/**
 * Get the lesson end time as HH:MM string.
 */
export const getLessonEndTime = (lesson: any): string => {
  if (!lesson?.end_datetime) return "";
  try {
    const end = new Date(lesson.end_datetime);
    return new Intl.DateTimeFormat("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Europe/Berlin",
    }).format(end);
  } catch {
    return "";
  }
};

/**
 * Clamp arrival time to be between lesson start (exclusive) and lesson end (exclusive).
 */
export const clampArrivalTime = (arrivalTime: string, lesson: any): string => {
  if (!arrivalTime || !lesson) return arrivalTime;
  try {
    const startTime = lesson.time;
    const endTime = getLessonEndTime(lesson);

    if (!startTime || !endTime) return arrivalTime;

    if (arrivalTime <= startTime) {
      const [sh, sm] = startTime.split(":").map(Number);
      const startDate = new Date();
      startDate.setHours(sh, sm + 1, 0, 0);
      return new Intl.DateTimeFormat("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/Berlin",
      }).format(startDate);
    }
    if (arrivalTime >= endTime) {
      const [eh, em] = endTime.split(":").map(Number);
      const endDate = new Date();
      endDate.setHours(eh, em - 1, 0, 0);
      return new Intl.DateTimeFormat("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/Berlin",
      }).format(endDate);
    }
    return arrivalTime;
  } catch {
    return arrivalTime;
  }
};
